import { memo, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { AnalyticsUtils, DemoChip, InfoTipPopover } from '@frontend/analytics-shared';
import { AreaChartData, Chart, sequentialChartColors } from '@frontend/charts';
import { useTranslation } from '@frontend/i18n';
import { formatters } from '@frontend/shared';
import { AiAssistant } from '@frontend/styled-icons';
import { theme } from '@frontend/theme';
import {
  DropdownFilterChipField,
  SwitchChipGroup,
  useFormField,
  useSwitchChipGroup,
  styles as dsStyles,
  Text,
} from '@frontend/design-system';
import {
  useFetchDeviceExtensionsReport,
  type DeviceExtensionsCallType,
} from '../../hooks/use-fetch-device-extensions-report';
import { useIsPhoneAnalyticsDemoAccount } from '../../hooks/use-is-phone-analytics-demo-account';
import { usePhoneAnalyticsShallowStore } from '../../hooks/use-phone-analytics-store';
import { trackingIds } from '../../tracking-ids';
import { ChartProps } from './shared-types';

type DeviceExtensionsFilterProps = {
  callType: DeviceExtensionsCallType;
  disabled?: boolean;
  extensions?: { list: string[]; renamedUUIDs: Record<string, string> };
  isSmallMode?: boolean;
  selectedExtensions: string[];
  setCallType: (value: DeviceExtensionsCallType) => void;
  setSelectedExtensions: (value: string[]) => void;
};

type ProcessedData = {
  chartData: AreaChartData;
  totals: Record<string, number>;
};

const PhoneExtensionChartFilters = memo(
  ({
    callType,
    disabled,
    extensions,
    isSmallMode,
    selectedExtensions,
    setCallType,
    setSelectedExtensions,
  }: DeviceExtensionsFilterProps) => {
    const { t } = useTranslation('analytics-phone');

    const switchChipProps = useSwitchChipGroup({
      defaultValue: [callType],
      onChange: (val) => {
        setCallType(val[0] as DeviceExtensionsCallType);
      },
    });

    const extensionsFieldProps = useFormField(
      {
        type: 'checklist',
        value: selectedExtensions,
      },
      [selectedExtensions]
    );

    return (
      <div className='no-pdf' css={styles.filtersWrapper}>
        <SwitchChipGroup {...switchChipProps} singleSelect>
          <SwitchChipGroup.Option
            className={isSmallMode ? 'small-mode-button' : undefined}
            disabled={disabled}
            trackingId={trackingIds.deviceExtensionsCallsAnswered}
            value='answered'
          >
            {t('Calls Answered')}
          </SwitchChipGroup.Option>
          <SwitchChipGroup.Option
            className={isSmallMode ? 'small-mode-button' : undefined}
            disabled={disabled}
            trackingId={trackingIds.deviceExtensionsCallsPlaced}
            value='placed'
          >
            {t('Calls Placed')}
          </SwitchChipGroup.Option>
        </SwitchChipGroup>

        <DropdownFilterChipField
          {...extensionsFieldProps}
          className={isSmallMode ? 'small-mode-dropdown' : undefined}
          icon='phone'
          label={t('Extensions')}
          name='extension-filter'
          onApply={({ value, close }) => {
            setSelectedExtensions(value);
            close();
          }}
          showClearAll={false}
          trackingIdBase={trackingIds.deviceExtensionsFilter}
        >
          {extensions?.list.map((extension) => (
            <DropdownFilterChipField.Option key={extension} value={extension}>
              <Text css={[dsStyles.truncate, styles.filterOption]}>
                {extensions.renamedUUIDs[extension] || extension}
              </Text>
            </DropdownFilterChipField.Option>
          ))}
        </DropdownFilterChipField>
      </div>
    );
  }
);

export const PhoneExtensionChart = memo(({ exportPdfProps, isSmallMode }: ChartProps) => {
  const { t } = useTranslation('analytics-phone');
  const isDemoAccount = useIsPhoneAnalyticsDemoAccount();
  const { filterHintText } = usePhoneAnalyticsShallowStore('filterHintText');
  const [extensions, setExtensions] = useState<string[]>([]);
  const [callType, setCallType] = useState<DeviceExtensionsCallType>('answered');
  const { data, isHourlyData, isLoading } = useFetchDeviceExtensionsReport();

  const processedData: ProcessedData = useMemo(() => {
    if (!extensions.length || !data?.records) {
      return {
        chartData: {
          groups: [],
        },
        totals: {},
      };
    }

    let groups: AreaChartData['groups'] = [];

    if (isHourlyData) {
      // isHourlyInsights will be true only when there is just one date record
      // Hence we can directly pick the first record set from the extensionsRecords
      const record = Object.values(data.records)[0] || {};
      groups = Object.entries(record).reduce<AreaChartData['groups']>((acc, [extension, values]) => {
        if (!extensions.includes(extension)) {
          return acc;
        }
        return AnalyticsUtils.formatHourlyInsights(extension, values[callType], { groups: acc });
      }, []);
    } else {
      groups = Object.entries(data.records || {}).map(([date, data]) => {
        return {
          name: formatters.date(date),
          values: extensions.reduce<Record<string, number>>((acc, extension) => {
            acc[extension] = AnalyticsUtils.sumArrayItems(data[extension]?.[callType]);
            return acc;
          }, {}),
        };
      });
    }

    return {
      chartData: {
        groups,
      },
      totals: groups.reduce<Record<string, number>>((acc, group) => {
        Object.entries(group.values).forEach(([extension, value]) => {
          acc[extension] = (acc[extension] || 0) + value;
        });
        return acc;
      }, {}),
    };
  }, [callType, data?.records, extensions, isHourlyData]);

  const chartColors = useMemo(() => {
    return data?.details?.list?.reduce<Record<string, string>>((acc, extension, index) => {
      acc[extension] = sequentialChartColors[index];
      return acc;
    }, {});
  }, [data?.details]);

  useEffect(() => {
    setExtensions(data?.details?.list || []);
  }, [data?.details?.list]);

  const chartLabels = useMemo(() => {
    const baseLabels = {
      ...(data?.details?.renamedUUIDs || {}),
      unknown: t('Unknown'),
    };

    const aiReceptionistLabel = (
      <div css={styles.aiReceptionistLabel}>
        <AiAssistant />
        <Text css={styles.aiReceptionistText}>{t('AI Receptionist')}</Text>
      </div>
    );

    const AI_RECEPTIONIST_KEY = 'ai receptionist';

    return {
      ...baseLabels,
      [AI_RECEPTIONIST_KEY]: aiReceptionistLabel,
    };
  }, [data?.details?.renamedUUIDs, t]);

  return (
    <Chart
      colors={chartColors}
      emptyStateConfig={{
        type: 'phone_extensions',
        label: t('Sorry! Phone extension report is not available'),
      }}
      isLoading={isLoading}
      labels={chartLabels}
    >
      <Chart.Header
        allowExportToPdf={exportPdfProps.allowExportToPdf}
        bottomElement={
          <PhoneExtensionChartFilters
            callType={callType}
            extensions={data?.details}
            isSmallMode={isSmallMode}
            selectedExtensions={extensions}
            setCallType={setCallType}
            setSelectedExtensions={setExtensions}
          />
        }
        infoTip={
          <InfoTipPopover>
            {t(
              'It shows which device extension answered or placed the call. It only includes first interaction of the call.'
            )}
          </InfoTipPopover>
        }
        leftElement={isDemoAccount && <DemoChip />}
        pdfDetails={[
          { label: t('Type'), value: callType === 'answered' ? t('Answered Calls') : t('Placed Calls') },
          ...exportPdfProps.pdfDetails,
        ]}
        subtitle={!isSmallMode && filterHintText}
        title={t('Device Extension')}
      />
      <Chart.AreaChart
        appearance={{
          margin: { left: 20 },
          showXAxis: true,
          showYAxis: true,
          tooltipLabelsTransform: 'capitalize',
          xAxisTickWidth: 120,
        }}
        data={processedData.chartData}
      />
      <Chart.Legends formatValue={formatters.value} labelsTransform='capitalize' values={processedData.totals} />
    </Chart>
  );
});

PhoneExtensionChartFilters.displayName = 'PhoneExtensionChartFilters';
PhoneExtensionChart.displayName = 'PhoneExtensionChart';

const styles = {
  filtersWrapper: css`
    margin: ${theme.spacing(2, 0)};
    align-items: center;
    display: flex;
    flex-wrap: wrap;
    gap: ${theme.spacing(1)};
    justify-content: space-between;

    .small-mode-button {
      max-width: 90px;
    }

    .small-mode-dropdown {
      max-width: 110px;

      > svg:first-of-type {
        display: none;
      }
    }
  `,

  filterOption: css`
    max-width: 200px;
    text-transform: capitalize;
  `,

  aiReceptionistLabel: css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
  `,

  aiReceptionistText: css`
    color: inherit;
    font-size: ${theme.font.size['medium']};
  `,
};
